(ns minorodata.transform.api
  (:require
   [clojure.string :as c.string]
   [minorodata.app.handlers :refer [dispatch reg-event-fx reg-fx]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as utils]
   [minorodata.group.model :refer [->group-transforms]]
   [minorodata.sandbox.model :refer [->pipeline-transform]]
   [minorodata.transform.model :refer [->transform ->transform-master
                                       ->transform-queue ->transform-version
                                       ->transform-versions-list
                                       ->transforms-panel ->unsaved-transform]]
   [mold-http.core :refer [reg-event-http]]
   [re-posh.core :as re-posh]))


;; =============================================================================
;; Load a single Transform
;; =============================================================================

(defn fetch-single-transform-request [_ [{:keys [group-id transform-id add-to-panel? on-finish on-success]
                                          :or   {add-to-panel? true}}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/transforms/" transform-id)
    :cb-params {:add-to-panel? add-to-panel?
                :on-finish on-finish
                :on-success on-success}}})

(defn fetch-single-transform-callback [_ [error resp {:keys [add-to-panel? on-finish on-success]}]]
  (when on-finish (on-finish))
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load transform"]
                  [:app.api/log :error "Failed to load transform" error]]}
    (let [transform resp]
      (when on-success (on-success transform))
      {:dispatch-n (cond-> [[:transform/add (->transform (update transform :tags #(or % [])))]]
                     add-to-panel?
                     (conj [:transform.events/add-transform-to-panel (:id transform)]))})))

(reg-event-http
 :transform.api/fetch-single-transform
 fetch-single-transform-request
 fetch-single-transform-callback)


;; =============================================================================
;; Load Transforms
;; =============================================================================

(defn fetch-query-params [params panel]
  (let [{:transforms-panel/keys [page order-by order-dir]} panel
        sort (cond-> nil
               (some? order-by) (assoc :order-by order-by)
               (some? order-dir) (assoc :order-dir order-dir)
               (some? (:order-by params)) (assoc :order-by (:order-by params))
               (some? (:order-dir params)) (assoc :order-dir (:order-dir params)))]
    (utils/assoc-some {:pagination (utils/assoc-some
                                    {:page (if (:replace? params)
                                             1
                                             (or (:page params) page 1))}
                                    :items-per-page (:items-per-page params))}
                      :sort sort)))


(defn fetch-transforms-request [_ [{:keys [group-id page replace? on-success]
                                    :or   {replace? false}
                                    :as   params}]]
  (let [panel @(re-posh/subscribe [:transforms-panel/pull])]
    (when (or (nil? page)
              (<= page (:transforms-panel/total-pages panel)))
      {:http-xhrio
       {:id        (str "fetch-transforms-request-" group-id)
        :strategy  :keep-latest
        :url       (str "/api/transformations/" group-id "/transforms")
        :params    (fetch-query-params params panel)
        :cb-params {:replace?   replace?
                    :group-id   group-id
                    :on-success on-success}}})))

(defn fetch-transforms-callback [_ [error resp {:keys [replace? group-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load transforms"]
                  [:app.api/log :error "Failed to load transforms" error]]}

    (let [{:transforms-panel/keys [latest-order-index]} @(re-posh/subscribe [:transforms-panel/pull])
          {:keys [results pagination sort]} resp
          results-count   (count results)
          [start-idx end-idx] (if replace?
                                [0 results-count]
                                [latest-order-index (+ latest-order-index results-count)])
          transforms      (mapv (fn [idx transform] (assoc transform :order idx))
                                (range start-idx end-idx)
                                results)
          panel           (-> {:transforms         transforms
                               :latest-order-index end-idx}
                              (merge pagination sort)
                              ->transforms-panel)
          ;; TODO this manual transform update is a quick fix for now, should be handled properly by mold library
          update-previous (mapv (fn [{:keys [id] :as transform}]
                                  [:transform/update [:transform/id id] (->transform transform) {:replace true}])
                                transforms)]
      (when on-success (on-success transforms))

      {:dispatch-n (conj update-previous
                         [:transforms-panel/update panel {:replace replace?}]
                         [:group-transforms/add (->group-transforms {:id         group-id
                                                                     :transforms transforms})])})))

(reg-event-http
 :transform.api/fetch-transforms
 fetch-transforms-request
 fetch-transforms-callback)


;; =============================================================================
;; Parse SQL v2
;; =============================================================================


(defn parse-sql-request-v2 [_ [{:keys [transform-id sql tab-id]}]]
  {:http-xhrio
   {:url       "/api/transformations/parse"
    :method    :post
    :params    {:sql sql}
    :cb-params {:transform-id transform-id
                :tab-id       tab-id}}})

(defn format-parser-v2-errors [errors]
  (clojure.string/join "\n" errors))

(defn parse-sql-callback-v2 [_ [error resp {:keys [transform-id tab-id]}]]
  (let [parse-error (get-in error [:response :parse-error])
        meta-tables-fn (fn [tables]
                         (fn [m]
                           (assoc m
                                  :transform-parsed-tables tables
                                  :transform-parsing-error nil)))
        meta-fn (fn [m]
                  (assoc m
                         :transform-parsing-error parse-error
                         :transform-parsed-tables nil))
        meta-with-message-fn (fn [message]
                               (fn [m]
                                 (assoc m
                                        :transform-parsing-error message
                                        :transform-parsed-tables nil)))]


    (cond
      (some? parse-error)
      {:dispatch-n [;; Trigger transform upsert flow
                    [:transform.api/parse-sql-v2-failure-trigger]
                    (when tab-id
                      [:sql-console-modal.events/update-metadata-for-tab tab-id meta-fn])
                    (when transform-id
                      [:transforms-panel/update
                       (->transforms-panel {:sql-parse-error (format-parser-v2-errors parse-error)})])]}

      (or (some? error)
          ;; Sometimes parsing lambda fails with 200 and returns stack trace as
          ;; :results. Can't finx this in the lambda since some backend error
          ;; handling code depends on this error being 200 and parses it.
          (string? (:results resp)))
      {:dispatch-n [;; Trigger transform upsert flow
                    [:transform.api/parse-sql-v2-failure-trigger]
                    [:app.events/add-snack :error "Failed to parse transform. Please contact support."]
                    (when transform-id
                      [:transforms-panel/update
                       (->transforms-panel {:sql-parse-error "Failed to parse transform."})])]}

      :otherwise
      (let [{:keys [input-tables output-tables]} (:results resp)
            inputs (when-not (empty? input-tables)
                     (clojure.string/join ", " input-tables))
            outputs (when-not (empty? output-tables)
                      (clojure.string/join ", " output-tables))

            success-message (str "Identified input tables:\n"
                                 (or inputs "None")
                                 ".\n"
                                 "Output tables:\n"
                                 (or outputs "None")
                                 ".")]

        {:dispatch-n [;; Trigger transform upsert flow
                      [:transform.api/parse-sql-v2-success-trigger]
                      [:app.events/add-snack :success success-message]
                      (when tab-id
                        [:sql-console-modal.events/update-metadata-for-tab tab-id (meta-tables-fn (:results resp))])

                      (when transform-id
                        [:unsaved-transform/update
                         [:unsaved-transform/id transform-id]
                         (->unsaved-transform (:results resp))])]}))))

(reg-event-http
 :transform.api/parse-sql-v2
 parse-sql-request-v2
 parse-sql-callback-v2)


;; Empty events to trigger a transforms upsert workflows
(reg-event-fx
 :transform.api/parse-sql-v2-success-trigger
 (fn [_]))

(reg-event-fx
 :transform.api/parse-sql-v2-failure-trigger
 (fn [_]))


;; =============================================================================
;; Detect deps tables
;; =============================================================================

(def empty-deps
  {:unsaved-transform/input-tables  []
   :unsaved-transform/output-tables []})


(defn extract-deps-request [_ [{:keys [transform-id sql]}]]
  {:http-xhrio
   {:url       "/api/transformations/regex-check"
    :method    :post
    :params    {:sql sql}
    :cb-params transform-id}})

(defn extract-deps-callback [_ [error resp transform-id]]
  (if (some? error)
    {:dispatch [:unsaved-transform/update [:unsaved-transform/id transform-id] empty-deps]}

    (let [deps (:results resp)]
      {:dispatch [:unsaved-transform/update
                  [:unsaved-transform/id transform-id]
                  {:unsaved-transform/input-tables  (:input-tables deps)
                   :unsaved-transform/output-tables (:output-tables deps)}]})))

(reg-event-http
 :transform.api/extract-deps
 extract-deps-request
 extract-deps-callback)


;; =============================================================================
;; Validate deps
;; =============================================================================


(defn validate-deps-request [_ [{:keys [input-tables]}]]
  {:http-xhrio
   {:url    "/api/transformations/validate-deps"
    :method :post
    :params {:input-tables input-tables}}})

(defn validate-deps-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:transforms-panel/update
                (->transforms-panel
                 {:dep-validation-status "invalid"
                  :dep-validation-error  "Failed to complete validation. Make sure the transform has been saved and contains valid SQL."})]}

    (let [{:keys [result not-found]} resp]
      (if (= "valid" result)
        {:dispatch [:transforms-panel/update
                    (->transforms-panel
                     {:dep-validation-status result
                      :dep-validation-error  ""})]}
        {:dispatch [:transforms-panel/update
                    (->transforms-panel
                     {:dep-validation-status result
                      :dep-validation-error  (str "Missing upstream dependencies: " (clojure.string/join ", " not-found))})]}))))

(reg-event-http
 :transform.api/validate-deps
 validate-deps-request
 validate-deps-callback)


;; =============================================================================
;; Save New Transform
;; =============================================================================

(defn select-tables [raw-tables parsed-tables]
  (cond
    (some? parsed-tables) parsed-tables
    (seq raw-tables) raw-tables
    :otherwise []))


(defn create-transform-request [_ [{:keys [group-id transform] :as params}]]
  (let [{:keys [id days hours minute]} transform
        unsaved-transform @(re-posh/subscribe [:unsaved-transform/pull [:unsaved-transform/id id]])
        {:unsaved-transform/keys [input-tables output-tables]} unsaved-transform]
    {:http-xhrio
     {:url    (str "/api/transformations/" group-id "/transforms")
      :method :post
      :params (-> transform
                  (assoc :schedule {:days days :hours hours :minute minute})
                  (dissoc :id :days :hours :minute)
                  utils/clean-up-query-recursive
                  (update :command #(or % ""))
                  (update :ignore-parser #(or % false))
                  (update :is-full #(or % false))
                  (update :is-temp #(or % false))
                  (update :status #(or % "active"))
                  (update :input-tables select-tables input-tables)
                  (update :output-tables select-tables output-tables))
      :cb-params params}}))

(defn create-transform-callback [_ [error resp {:keys [on-success new-page?]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to create transform"]
                  [:app.api/log :error "Failed to create transform" error]]}

    (let [{:transforms-panel/keys [latest-order-index]} @(re-posh/subscribe [:transforms-panel/pull])
          {:keys [id group-id] :as transform} (:data resp)
          transform-idx  (or latest-order-index 0)
          full-transform (-> transform
                             (assoc :order transform-idx)
                             ->transform)
          group          @(re-posh/subscribe [:group-transforms/pull [:group-transforms/id group-id]])]
      (when on-success (on-success full-transform))
      {:dispatch-n (cond->
                    [[:transform/add full-transform]
                     [:unsaved-transform/delete [:unsaved-transform/id "new-transform"]]
                     [:transforms-panel/update {:transforms-panel/transforms         {:transform/id id}
                                                :transforms-panel/latest-order-index (+ transform-idx 1)}]
                     [:app.events/add-snack :success "Transform created"]
                     [:transformations.api/cyclic-deps-check {:show-warnings true}]
                     [:group-transforms/update
                      [:group-transforms/id (:group-transforms/id group)]
                      (update group
                              :group-transforms/transforms #(concat % [full-transform]))]]
                     (not new-page?) (concat [[:app.events/navigate :transform-details {:group-id     group-id
                                                                                        :transform-id id}]]))
       :posthog/capture {:event (:transform/created posthog-events/events)}})))

(reg-event-http
 :transform.api/create-transform
 create-transform-request
 create-transform-callback)


;; =============================================================================
;; Update Transform
;; =============================================================================

(defn- scheduled-cron? [schedule-spec]
  (let [cron (:cron schedule-spec)]
    (and (seq cron) (not= cron utils/empty-cron))))

(defn schedule-change-capture [scope previous-spec new-spec]
  (let [before? (scheduled-cron? previous-spec)
        after?  (scheduled-cron? new-spec)]
    (cond
      (and after? (not before?))
      {:event (:transform/scheduled posthog-events/events)
       :props {:scope scope}}

      (and before? (not after?))
      {:event (:transform/schedule-updated posthog-events/events)
       :props {:scope scope :action "unscheduled"}}

      (and before? after? (not= (:cron previous-spec) (:cron new-spec)))
      {:event (:transform/schedule-updated posthog-events/events)
       :props {:scope scope :action "changed"}})))

(defn update-transform-request [_ [{:keys [group-id transform redirect? on-success]
                                    :or {redirect? true}}]]
  (let [{:keys [id tags]} transform
        unsaved-transform @(re-posh/subscribe [:unsaved-transform/pull [:unsaved-transform/id id]])
        {:unsaved-transform/keys [input-tables output-tables]} unsaved-transform]
    {:http-xhrio
     {:url       (str "/api/transformations/" group-id "/transforms/" (:id transform))
      :method    :put
      :params    (-> transform
                     (dissoc :id :cron)
                     utils/clean-up-query-recursive
                     (update :tags select-tables tags)
                     (update :input-tables select-tables input-tables)
                     (update :output-tables select-tables output-tables)
                     (update :command #(or % "")))
      :cb-params {:old-transform-id (:id transform)
                  :redirect?        redirect?
                  :on-success       on-success}}}))

(defn update-transform-callback [_ [error resp {:keys [old-transform-id redirect? on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it. Please save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id old-transform-id] {:transform/stale true}]]}

    (some? error)
    (let [message (or (get-in error [:response :message])
                      (get-in error [:response :error]))]
      {:dispatch-n [[:app.events/add-snack :error (if message
                                                    (str "Failed to update the transform.\n" message)
                                                    "Failed to update the transform")]
                    [:app.api/log :error "Failed to update the transform" error]]})

    :otherwise
    (let [{:keys [id group-id] :as transform} (:data resp)
          previous-spec @(re-posh/subscribe [:transform/prop [:transform/id old-transform-id] :transform/schedule-spec])]
      (when on-success (on-success))
      {:fx [[:posthog/capture {:event (:transform/updated posthog-events/events)}]
            (when-let [capture (schedule-change-capture "transform" previous-spec (:schedule-spec transform))]
              [:posthog/capture capture])]
       :dispatch-n (cond-> [[:unsaved-transform/delete [:unsaved-transform/id old-transform-id]]
                            [:transform/update [:transform/id old-transform-id] (->transform transform) {:replace true}]
                            [:pipeline-transform/update [:pipeline-transform/id old-transform-id] {:pipeline-transform/stale true}]
                            [:app.events/add-snack :success "Transform updated"]
                            [:sql-console-modal.events/update-transform-ids old-transform-id transform]
                            [:transformations.api/cyclic-deps-check
                             {:on-cycle-ev [:transform.events/toggle-circular-dependency-modal true transform]}]]
                     redirect?
                     (conj [:app.events/navigate :transform-details {:group-id     group-id
                                                                     :transform-id id}]))})))

(reg-event-http
 :transform.api/update-transform
 update-transform-request
 update-transform-callback)


;; =============================================================================
;; Get Latest Transform Version
;; =============================================================================

(defn get-latest-version-request [_ [{:keys [group-id transform-id]}]]
  (let [master-id @(re-posh/subscribe [:transform/prop [:transform/id transform-id] :transform/master-id])]
    {:http-xhrio
     {:url       "/api/transformations/latest-version"
      :method    :get
      :params    {:master-id master-id}
      :cb-params {:group-id     group-id
                  :transform-id transform-id}}}))

(defn get-latest-version-callback [_ [error resp {:keys [group-id transform-id]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transform"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform resp]
      {:dispatch-n [[:transform/update [:transform/id transform-id] (->transform transform) {:replace true}]
                    [:app.events/navigate :transform-details {:group-id     group-id
                                                              :transform-id (:id transform)}]]})))

(reg-event-http
 :transform.api/get-latest-version
 get-latest-version-request
 get-latest-version-callback)



;; =============================================================================
;; Get Latest Transform Version By Transform Id
;; =============================================================================

(defn get-latest-version-by-master-id-request [_ [{:keys [master-id old-transform-id on-success] :as params}]]
  {:http-xhrio
   {:url       "/api/transformations/latest-version"
    :method    :get
    :params    {:master-id master-id}
    :cb-params {:old-transform-id old-transform-id
                :params           params
                :on-success       on-success}}})

(defn get-latest-version-by-master-id-callback [_ [error resp cb-params]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transform"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform (-> resp
                        (assoc :id (:transform-id resp)))
          parsed-transform (->transform transform)]
      (when (fn? (:on-success cb-params)) ((:on-success cb-params) parsed-transform))
      {:dispatch-n [[:transform/update [:transform/id (:id transform)] parsed-transform {:replace true}]
                    (when (:old-transform-id cb-params)
                      [:sql-console-modal.events/update-transform-ids (:old-transform-id cb-params) (assoc transform
                                                                                                           :transform-id (:id transform))])
                    [:transform-master/add (->transform-master {:master-id (:master-id transform)
                                                                :transform transform})]
                    [:pipeline-transform/add (->pipeline-transform transform)]]})))

(reg-event-http
 :transform.api/get-latest-version-by-master-id
 get-latest-version-by-master-id-request
 get-latest-version-by-master-id-callback)


(defn get-latest-version-by-master-id-preview-callback [_ [error resp cb-params]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch transform"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform (-> resp
                        (assoc :id (:transform-id resp)))
          old-transform-id (:old-transform-id cb-params)]
      {:dispatch-n [[:transform.events/save-transform-production-preview (:master-id transform) transform]
                    (when old-transform-id
                      [:transform/update [:transform/id old-transform-id] (->transform transform) {:replace true}])

                    (when old-transform-id
                      [:sql-console-modal.events/update-transform-ids
                       old-transform-id
                       (assoc transform :transform-id (:id transform))])]})))


;; This "preview" event doesn't update the underlying transform model entry,
;; and just stores the data for GUI purposes. That way we don't mess with
;; environments.
(reg-event-http
 :transform.api/get-latest-version-by-master-id-preview
 get-latest-version-by-master-id-request
 get-latest-version-by-master-id-preview-callback)



(defn get-latest-version-by-master-id-preview-and-run [_ [error resp cb-params]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch latest transform version"]
                  [:app.api/log :error "Failed to fetch transform" error]]}

    (let [transform-id (:transform-id resp)
          master-id (:master-id resp)
          params    (:params cb-params)
          {:keys [type]} params]
      {:dispatch-n [[:transform.api/start-by-id
                     {:transform-id transform-id
                      :type type
                      :master-id master-id}]]})))

(reg-event-http
 :transform.api/get-latest-version-by-master-id-and-run
 get-latest-version-by-master-id-request
 get-latest-version-by-master-id-preview-and-run)


;; =============================================================================
;; Delete Transform
;; =============================================================================

(defn delete-transform-request [_ [{:keys [group-id transform-id navigate? silent? on-success]
                                    :or   {navigate? true}}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/transforms/" transform-id)
    :method    :delete
    :cb-params {:group-id     group-id
                :transform-id transform-id
                :navigate?    navigate?
                :silent?      silent?
                :on-success   on-success}}})

(defn delete-transform-callback [_ [error _ {:keys [group-id transform-id navigate? silent? on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it.
                    Please save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id transform-id] {:transform/stale true}]]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to delete the transform"]
                  [:app.api/log :error "Failed to delete the transform" error]]}

    :otherwise
    (do
      (when on-success (on-success))
      {:dispatch-n [[:transform/delete [:transform/id transform-id]]
                    (when-not silent?
                      [:app.events/add-snack :success "Transform deleted"])
                    (when navigate?
                      [:app.events/navigate :transforms {:group-id group-id}])]})))

(reg-event-http
 :transform.api/delete-transform
 delete-transform-request
 delete-transform-callback)


;; =============================================================================
;; Move Transform To Group
;; =============================================================================

(defn move-to-group-request [_ [{:keys [to-group transform-id redirect? silent? on-success]
                                 :or {redirect? true}}]]
  {:http-xhrio
   {:url       "/api/transformations/move-transform"
    :method    :post
    :params    {:target-group to-group
                :transform-id transform-id}
    :cb-params {:transform-id transform-id
                :group-id   to-group
                :on-success on-success
                :silent?    silent?
                :redirect?  redirect?}}})

(defn move-to-group-callback [_ [error _ {:keys [transform-id group-id on-success silent? redirect?]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to move transform"]
                  [:app.api/log :error "Failed to move transform" error]]}

    (do
      (when on-success (on-success))
      {:dispatch-n [(when (not silent?) [:app.events/add-snack :success "Transform moved"])
                    (when redirect? [:app.events/navigate :transforms {:group-id group-id}])
                    [:transform/delete [:transform/id transform-id]]]})))

(reg-event-http
 :transform.api/move-to-group
 move-to-group-request
 move-to-group-callback)


;; =============================================================================
;; Unschedule Transform
;; =============================================================================

(defn unschedule-transform-request [_ [{:keys [group-id transform on-success redirect?]}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/transforms/" (:id transform))
    :method    :put
    :params    (-> transform
                   (dissoc :id)
                   utils/clean-up-query-recursive
                   (dissoc :schedule)
                   (update :tags select-tables nil)
                   (update :input-tables select-tables nil)
                   (update :output-tables select-tables nil))
    :cb-params {:group-id     group-id
                :transform-id (:id transform)
                :redirect?    redirect?
                :on-success   on-success}}})

(defn unschedule-transform-callback [_ [error result {:keys [group-id transform-id redirect? on-success]
                                                      :or   {redirect? true}}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it.
                    Please save your changes, and reload the page to get the latest version."]
                  [:transform/update [:transform/id transform-id] {:transform/stale true}]]}

    (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to unschedule transform"]
                  [:app.api/log :error "Failed to unschedule transform" error]]}

    :otherwise
    (let [{:keys [id] :as transform} (:data result)]
      (when on-success
        (on-success {:transform transform}))
      {:dispatch-n (cond-> [[:transform/update [:transform/id transform-id] (->transform transform) {:replace true}]
                            [:app.events/add-snack :success "Transform unscheduled"]]
                     redirect? (concat [[:app.events/navigate :transform-details {:group-id     group-id
                                                                                  :transform-id id}]]))
       :posthog/capture {:event (:transform/schedule-updated posthog-events/events)
                         :props {:scope "transform" :action "unscheduled"}}})))

(reg-event-http
 :transform.api/unschedule-transform
 unschedule-transform-request
 unschedule-transform-callback)


;; =============================================================================
;; Toaster Poll
;; =============================================================================

(def TOASTER_POLLING_INTERVAL_MS 2500)


(defn toaster-poll-request [_ [job-id]]
  {:http-xhrio
   {:url       (str "/api/toaster/job-execution/" job-id)
    :cb-params {:job-id job-id}}})


(defn toaster-poll-callback [_ [error resp {:keys [job-id]}]]
  (if (some? error)
    {:dispatch-n [[:job/delete [:job/id job-id]]
                  [:app.events/add-snack :error "Failed to fetch job status"]
                  [:app.api/log :error "Failed to fetch job status" error]]}

    (let [results   (:results resp)
          job-id    (:job-execution/id results)
          status    (:job/status results)
          finished? (or (= "failed" status)
                        (= "success" status))]
      (utils/assoc-some
       {:dispatch-n [[:transformations.events/set-job-statuses results]
                     (when finished? [:connections.api/fetch-transform-statuses])]}
       :dispatch-later (when-not finished?
                         [{:ms       TOASTER_POLLING_INTERVAL_MS
                           :dispatch [:transform.api/poll-by-id-old job-id]}])))))

(reg-event-http
 :transform.api/poll-by-id-old
 toaster-poll-request
 toaster-poll-callback)

;; =============================================================================
;; Toaster Start Transform
;; =============================================================================

(defn toaster-start-request [_ [{:keys [transform-id type environment-id]
                                 :or  {type :single}}]]

  (let [current-user @(re-posh/subscribe [:user/pull])
        client-id    (:user/cid current-user)]
    {:http-xhrio
     {:url       "/api/toaster/job/one-time"
      :method    :post
      :params    {"client/id" client-id
                  "job/tasks" [{"task/type"   "job-builder"
                                "task/params" (cond-> {"transform/type" type
                                                       "transform/ids"  [transform-id]}
                                                environment-id (assoc "environment/id" environment-id))}]}
      :cb-params {:transform-id transform-id
                  :type         type}}
     :posthog/capture {:event (:transform/run-triggered posthog-events/events)
                       :props (cond-> {:transform-id transform-id
                                       :type         (str type)}
                                environment-id (assoc :environment-id environment-id))}}))


(defn toaster-start-callback [_ [error resp {:keys [transform-id type]}]]
  (if (some? error)
    (let [message (if (= :single type)
                    "Failed to start the transform"
                    "Failed to start transforms")]
      {:dispatch-n [[:app.events/add-snack :error message]
                    [:app.api/log :error message error]]})

    (let [results        (:results resp)
          job-builder-id (:job-execution/id results)

          transform @(re-posh/subscribe [:transform/pull [:transform/id transform-id]])
          {:transform/keys [name]} transform

          text           (if (= :single type)
                           (str "Started transform " name)
                           (str "Initiated the job for running all transforms"))]
      {:dispatch-n [[:app.events/add-snack :success text]
                    [:transformations.events/add-initial-job results transform-id]
                    [:transform.api/poll-by-id-old job-builder-id]]})))


(reg-event-http
 :transform.api/start-by-id
 toaster-start-request
 toaster-start-callback)

;; =============================================================================
;; Transform versions
;; =============================================================================

(defn ordered-models [{:keys [index-model results replace? to-model]}]
  (let [index-model-index  (keyword index-model "latest-order-index")
        index-model-pull   (keyword index-model "pull")
        index-model-inst   @(re-posh/subscribe [index-model-pull])
        latest-order-index (get index-model-inst index-model-index)
        results-count      (count results)

        [start-idx end-idx] (if replace?
                              [0 results-count]
                              [latest-order-index (+ latest-order-index results-count)])

        models             (mapv (fn [idx item] (to-model (assoc item :order idx)))
                                 (range start-idx end-idx)
                                 results)]
    {:models             models
     :latest-order-index end-idx}))


(defn fetch-transform-versions-request [_ [master-id {:keys [page total-pages]}]]
  (if (some? total-pages)
    (when (<= page total-pages)
      {:http-xhrio
       {:url    (str "/api/transformations/transform-versions/" master-id)
        :params {:pagination {:page page}}}})

    {:http-xhrio
     {:url    (str "/api/transformations/transform-versions/" master-id)
      :params {:pagination {:page 1}}}}))


(defn fetch-transform-versions-callback [_ [error resp]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to fetch the transform history"]
                  [:app.api/log :error "Failed to fetch the transform history"]]}

    (let [{:keys [results pagination]} resp
          {:keys [models latest-order-index]} (ordered-models {:index-model :transform-versions-list
                                                               :results     results
                                                               :replace?    false
                                                               :to-model    ->transform-version})
          versions-list (->transform-versions-list
                         {:total-pages        (:total-pages pagination)
                          :page               (:page pagination)
                          :items-per-page     (:items-per-page pagination)
                          :latest-order-index latest-order-index})]
      {:dispatch-n [[:transform-version/add-many models]
                    [:transform-versions-list/update versions-list {:replace true}]]})))

(reg-event-http
 :transform.api/fetch-transform-versions
 fetch-transform-versions-request
 fetch-transform-versions-callback)



;; =============================================================================
;; Fetch all transform statuses
;; =============================================================================


(defn fetch-transform-statuses-request [_ _]
  {:http-xhrio
   {:id     :fetch-transform-statuses-request
    :url    (str "/api/transformations/statuses")
    :method :get}})

(defn fetch-transform-statuses-callback [_ [error resp]]
  (if (some? error)
    (let [message "Failed to fetch transforms' statuses"]
      {:dispatch-n [[:app.events/add-snack :error message]
                    [:app.api/log :error message error]]})

    (let [statuses      (:statuses resp)]

      {:dispatch [:transform-status-list/add {:transform-status-list/statuses statuses}]})))

(reg-event-http
 :connections.api/fetch-transform-statuses-request
 fetch-transform-statuses-request
 fetch-transform-statuses-callback)

;; =============================================================================
;; Transform status poll
;; =============================================================================


;; Hack, temp fix to reduce server load
(defonce throttle-timeouts (atom {}))

(reg-fx
 :dispatch-throttle
 (fn [[id event-vec n]]
   (let [[existing-timeout initial?] (get @throttle-timeouts id)]
     (if (some? existing-timeout)
       ;; if there's a timeout blocking execution, clear previous timeout if
       ;; it was the initial (the one which just clears the timeout at the
       ;; end)
       (when initial?
         (js/clearTimeout existing-timeout)

         ;; and make sure we execute the event when the timer ends
         (swap! throttle-timeouts assoc id
                [(js/setTimeout (fn []
                                  (dispatch event-vec)
                                  (swap! throttle-timeouts dissoc id))
                                n)
                 false]))

       (do
         ;; dispatch event
         (dispatch event-vec)

         ;; and start new interval to cleat the timeout flag
         (swap! throttle-timeouts assoc id
                [(js/setTimeout (fn []
                                  (swap! throttle-timeouts dissoc id))
                                n)
                 true]))))))


(reg-event-fx
 :connections.api/fetch-transform-statuses
 (fn [_ [params]]
   {:dispatch-throttle [:fetch-transform-statuses
                        [:connections.api/fetch-transform-statuses-request params]
                        5000]}))

(defn fetch-transform-queue-request []
  {:http-xhrio
   {:id     :fetch-transform-queue-request
    :url    "/api/transformations/queue"
    :method :get}})

(defn fetch-transform-queue-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch transforms queue."]}
    {:dispatch-n [[:transform-queue/delete]
                  [:transform-queue/add (->transform-queue resp)]]}))

(reg-event-http
 :transform.api/fetch-transform-queue
 fetch-transform-queue-request
 fetch-transform-queue-callback)
